import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetClientActivities } from '@/api/clients/get-client-activities'
import { useGetClientProfile } from '@/api/clients/get-client-profile'
import { TableGroup } from '@/components/layout/table-group'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Select } from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { DEFAULT_TZ } from '@/constants/date'
import { useFilters } from '@/hooks'

import {
  ALL_ACTIVITIES_VALUE,
  INITIAL_FILTERS,
  makeCategoryOptions,
} from './recent-activities.constants'
import * as S from './recent-activities.styled'
import { ActivityType } from './recent-activities.types'
import { filterActivities, getColumns } from './recent-activities.utils'

interface RecentActivitiesProps {
  clientId: string
}

export const RecentActivities = ({ clientId }: RecentActivitiesProps) => {
  const { t } = useTranslation(['clients', 'common'])
  // Note: sourced from the resident's own building, not the globally selected
  // one — switching the top building selector while a profile is open must
  // not change the timezone this resident's activity timestamps render in.
  const { data: clientData } = useGetClientProfile({ clientId })
  const timezone = clientData?.buildingId.timezone ?? DEFAULT_TZ

  const { data: allActivities = [], isFetching } = useGetClientActivities({ clientId })

  const filters = useFilters({ initialState: INITIAL_FILTERS })
  const { categories, dateRange, skip, limit } = filters.filters

  const filtered = useMemo(
    () => filterActivities(allActivities, categories, dateRange),
    [allActivities, categories, dateRange],
  )

  const paginated = useMemo(() => filtered.slice(skip, skip + limit), [filtered, skip, limit])

  const handleCategoryChange = (newValue: string[]) => {
    const hadAll = categories.length === 0
    const hasAll = newValue.includes(ALL_ACTIVITIES_VALUE)
    if (!hadAll && hasAll) {
      filters.setFilter('categories', [])
      return
    }
    filters.setFilter(
      'categories',
      newValue.filter((v) => v !== ALL_ACTIVITIES_VALUE) as ActivityType[],
    )
  }

  const handleDateRangeChange = (range: [Date | null, Date | null]) => {
    filters.setFilter('dateRange', range)
  }

  return (
    <S.Container>
      <TableGroup
        noShadow
        id="client-recent-activities"
        data={paginated}
        loading={isFetching}
        columns={getColumns(timezone, t)}
        getRowKey={(item) => `${item.historyType}-${item.id}`}
        tableFilters={filters}
        pagination={{ paginationFilters: filters, total: filtered.length }}
        enableColumnVisibility={false}
        navigation={{
          datePicker: (
            <DatePickerInput
              mode="range"
              size="small"
              placeholder={t('clients:recentActivities.dateRangePlaceholder')}
              value={dateRange}
              onRangeChange={handleDateRangeChange}
            />
          ),
          statusTabs: (
            <Typography as="h2" size="heading-small" color="black800">
              {t('clients:recentActivities.title')}
            </Typography>
          ),
        }}
        filters={
          <Select
            type="multi"
            options={makeCategoryOptions(t)}
            value={categories.length === 0 ? [ALL_ACTIVITIES_VALUE] : categories}
            placeholder={t('clients:recentActivities.categoryPlaceholder')}
            size="small"
            onChange={handleCategoryChange}
          />
        }
        emptyNoFilters={{
          description: t('clients:recentActivities.empty.noFilters.description'),
          title: t('clients:recentActivities.empty.noFilters.title'),
        }}
        emptyWithFilters={{
          description: t('common:table.emptyWithFilters.description'),
          title: t('common:table.emptyWithFilters.title'),
        }}
      />
    </S.Container>
  )
}
